/**
 * UI Utilities for Focus and Selection Management
 */

import { logger } from '@/utils/logger';
/**
 * Saves the current focus state, including selection/caret position
 * @returns {object|null} Focus state object or null if no relevant focus
 */
export interface FocusState {
    field: string;
    rowId?: string;
    sceneId?: string;
    actId?: string;
    propId?: string;
    personnelId?: string;
    charId?: string;
    offset: number;
    selectionEnd: number;
    tagName: string;
    isContentEditable: boolean;
}

export function saveFocus(): FocusState | null {
    const active = document.activeElement as HTMLElement | null;
    if (!active) return null;

    const isContentEditable = active.hasAttribute('contenteditable');
    const isInput = active.tagName === 'INPUT' || active.tagName === 'TEXTAREA';
    if (!isContentEditable && !isInput) return null;

    const field = active.dataset.field;
    if (!field) return null; // We need a field to identify it

    const rowId = active.dataset.rowId || (active.closest('[data-row-id]') as HTMLElement)?.dataset.rowId;
    const sceneId = active.dataset.sceneId || (active.closest('[data-scene-id]') as HTMLElement)?.dataset.sceneId;
    const actId = active.dataset.actId || (active.closest('[data-act-id]') as HTMLElement)?.dataset.actId;
    const propId = active.dataset.propId || (active.closest('[data-prop-id]') as HTMLElement)?.dataset.propId;
    const personnelId = active.dataset.personnelId || (active.closest('[data-personnel-id]') as HTMLElement)?.dataset.personnelId;
    const charId = active.dataset.charId || (active.closest('[data-char-id]') as HTMLElement)?.dataset.charId;

    const selection = window.getSelection();
    let offset = 0;
    let selectionEnd = 0;

    if (active.hasAttribute('contenteditable')) {
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            offset = range.startOffset;
        }
    } else {
        // Standard input/textarea
        const input = active as HTMLInputElement;
        offset = input.selectionStart || 0;
        selectionEnd = input.selectionEnd || 0;
    }

    return {
        field,
        rowId,
        sceneId,
        actId,
        propId,
        personnelId,
        charId,
        offset,
        selectionEnd,
        tagName: active.tagName,
        isContentEditable: active.hasAttribute('contenteditable')
    };
}

/**
 * Restores focus and cursor position based on a saved state
 * @param {object} state - The state returned by saveFocus
 */
export function restoreFocus(state: FocusState | null) {
    if (!state) return;

    let selector = '';
    if (state.rowId) selector = `tr[data-row-id="${state.rowId}"]`;
    else if (state.propId) selector = `tr[data-prop-id="${state.propId}"]`;
    else if (state.personnelId) selector = `tr[data-personnel-id="${state.personnelId}"]`;
    else if (state.charId) selector = `tr[data-char-id="${state.charId}"]`;
    else if (state.sceneId) selector = `[data-scene-id="${state.sceneId}"]`;
    else if (state.actId) selector = `[data-act-id="${state.actId}"]`;

    if (!selector) {
        // Check for header inputs by ID
        if (state.field === 'date') selector = '#meta-date';
        else if (state.field === 'director') selector = '#meta-director';
        else if (state.field === 'sm') selector = '#meta-sm';
        else if (state.field === 'asm') selector = '#meta-asm';
        else if (state.field === 'showTitle') selector = '#input-show-title';
    }

    let input: HTMLElement | null = null;
    if (selector.startsWith('#')) {
        input = document.querySelector(selector);
    } else {
        const container = document.querySelector(selector) as HTMLElement;
        if (container) {
            input = container.querySelector(`[data-field="${state.field}"]`) as HTMLElement || 
                    (container.dataset.field === state.field ? container : null);
        }
    }

    if (!input) return;

    input.focus();

    // Restore caret position
    try {
        if (state.isContentEditable) {
            const selection = window.getSelection();
            const range = document.createRange();
            if (input.childNodes.length > 0) {
                const targetNode = input.childNodes[0] as Text;
                const targetOffset = Math.min(state.offset, targetNode.length || 0);
                range.setStart(targetNode, targetOffset);
                range.collapse(true);
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
        } else if (typeof (input as HTMLInputElement).setSelectionRange === 'function') {
            (input as HTMLInputElement).setSelectionRange(state.offset, state.selectionEnd);
        }
    } catch (error: unknown) {
        logger.warn("Failed to restore caret position", error);
    }
}

/**
 * Generates a deterministic SVG avatar data URI from a name
 * @param name - User's display name or email
 * @returns Data URI string (image/svg+xml)
 */
export function generateAvatar(name: string): string {
    const parts = name.trim().split(/\s+/);
    let initials = (parts[0]?.[0] || 'U').toUpperCase();
    if (parts.length > 1 && parts[parts.length - 1]![0]) {
        initials += parts[parts.length - 1]![0]!.toUpperCase();
    }
    
    // Deterministic color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    // Use a pleasant palette: 60-70% saturation, 45-55% lightness
    const color = `hsl(${hue}, 65%, 50%)`; 
    
    // Create SVG with initials
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <rect width="100" height="100" fill="${color}" />
  <text x="50" y="50" dy=".35em" text-anchor="middle" fill="white" font-family="sans-serif" font-size="45" font-weight="bold">${initials}</text>
</svg>`;
    
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}














